import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Calendar,
  Zap,
  Users,
  DollarSign,
  Settings,
  Shield,
  TrendingUp,
  HelpCircle,
  Download,
  Share2,
  Building2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scoreLabels = {
  C: { name: "Customer", color: "bg-blue-500", icon: Users },
  O: { name: "Operations", color: "bg-green-500", icon: Settings },
  M: { name: "Monetization", color: "bg-amber-500", icon: DollarSign },
  P: { name: "Promotion", color: "bg-purple-500", icon: TrendingUp },
  A: { name: "Automation", color: "bg-cyan-500", icon: Zap },
  N: { name: "Navigation", color: "bg-orange-500", icon: Shield },
  E: { name: "Expansion", color: "bg-pink-500", icon: TrendingUp },
  Z: { name: "Efficiency", color: "bg-indigo-500", icon: Target },
  E2: { name: "Exit Value", color: "bg-red-500", icon: DollarSign },
  CBIS: { name: "Business Index", color: "bg-blue-900", icon: Target },
};

const severityColors = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-500",
  low: "bg-green-500"
};

const severityBgColors = {
  critical: "bg-red-50 border-red-200",
  high: "bg-orange-50 border-orange-200",
  medium: "bg-amber-50 border-amber-200",
  low: "bg-green-50 border-green-200"
};

const CatalystResults = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadRun();
  }, [runId]);

  const loadRun = async () => {
    try {
      const response = await axios.get(`${API}/catalyst/run/${runId}`);
      setRun(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 65) return "text-amber-600";
    if (score >= 50) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-100";
    if (score >= 65) return "bg-amber-100";
    if (score >= 50) return "bg-orange-100";
    return "bg-red-100";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-900 mx-auto mb-4" />
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Run Not Found</h2>
            <p className="text-gray-600 mb-4">{error || 'The requested Catalyst run could not be found.'}</p>
            <Button onClick={() => navigate('/catalyst')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Catalyst
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { scores, determinations, program, company_name, industry, created_at, answers } = run;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/catalyst')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{company_name}</h1>
                <p className="text-blue-200">{industry} • {new Date(created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`text-center px-6 py-3 rounded-xl ${getScoreBg(scores?.CBIS)}`}>
                <div className="text-sm text-gray-600">CBIS Score</div>
                <div className={`text-3xl font-bold ${getScoreColor(scores?.CBIS)}`}>
                  {scores?.CBIS || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm p-1 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="scores" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Scores
            </TabsTrigger>
            <TabsTrigger value="determinations" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Determinations
            </TabsTrigger>
            <TabsTrigger value="sprint" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />
              90-Day Sprint
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              12-Month Roadmap
            </TabsTrigger>
            <TabsTrigger value="faq" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Executive Plan */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-900" />
                    Executive Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {program?.executivePlan || 'No executive plan available.'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-900">{scores?.CBIS || 0}</div>
                    <div className="text-sm text-gray-600">CBIS Score</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">
                      {determinations?.filter(d => d.severity === 'critical').length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Critical Issues</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">
                      {determinations?.filter(d => d.severity === 'high').length || 0}
                    </div>
                    <div className="text-sm text-gray-600">High Priority</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {program?.nextActions?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Action Items</div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Actions */}
              {program?.nextActions && program.nextActions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Recommended Next Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {program.nextActions.map((action, idx) => (
                        <Button
                          key={idx}
                          variant={action.priority === 1 ? "default" : "outline"}
                          className={action.priority === 1 ? "bg-blue-900 hover:bg-blue-800" : ""}
                        >
                          {action.label}
                          <Badge className="ml-2 bg-amber-500 text-white">P{action.priority}</Badge>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Scores Tab */}
          <TabsContent value="scores">
            <Card>
              <CardHeader>
                <CardTitle>COMPANEZE Score Breakdown</CardTitle>
                <CardDescription>
                  9 dimensions that determine your business readiness for growth
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {scores && Object.entries(scores).map(([key, value]) => {
                    const label = scoreLabels[key];
                    if (!label) return null;
                    const Icon = label.icon;
                    
                    return (
                      <div
                        key={key}
                        className={`p-4 rounded-xl border ${getScoreBg(value)}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-8 h-8 ${label.color} rounded-lg flex items-center justify-center`}>
                            <span className="text-white font-bold text-xs">{key}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{label.name}</span>
                        </div>
                        <div className={`text-3xl font-bold ${getScoreColor(value)}`}>
                          {value}
                        </div>
                        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${value >= 65 ? 'bg-green-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Determinations Tab */}
          <TabsContent value="determinations">
            <div className="space-y-4">
              {determinations && determinations.length > 0 ? (
                determinations.map((det, idx) => (
                  <Card key={idx} className={`border ${severityBgColors[det.severity]}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${severityColors[det.severity]} rounded-lg flex items-center justify-center`}>
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{det.title}</h3>
                            <Badge className={severityColors[det.severity]}>{det.severity.toUpperCase()}</Badge>
                          </div>
                        </div>
                        <Badge variant="outline">{det.owner}</Badge>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <span className="font-medium text-gray-700">Why: </span>
                          <span className="text-gray-600">{det.why}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">What to do: </span>
                          <span className="text-gray-600">{det.whatToDo}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900">No Critical Issues Found</h3>
                    <p className="text-gray-600">Your business is in good shape!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* 90-Day Sprint Tab */}
          <TabsContent value="sprint">
            <div className="space-y-6">
              {program?.sprint90Days && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-900" />
                        Weeks 1-2: Quick Wins
                      </CardTitle>
                      <CardDescription>Immediate actions to build momentum</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {(program.sprint90Days.week1_2 || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-900" />
                        Weeks 3-6: Foundation Building
                      </CardTitle>
                      <CardDescription>Establishing core systems and processes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {(program.sprint90Days.week3_6 || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-900" />
                        Weeks 7-12: Scale Preparation
                      </CardTitle>
                      <CardDescription>Getting ready for growth</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {(program.sprint90Days.week7_12 || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </TabsContent>

          {/* 12-Month Roadmap Tab */}
          <TabsContent value="roadmap">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {program?.roadmap12Months && ['q1', 'q2', 'q3', 'q4'].map((quarter, qIdx) => (
                <Card key={quarter}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-900" />
                      {quarter.toUpperCase()} - {['Jan-Mar', 'Apr-Jun', 'Jul-Sep', 'Oct-Dec'][qIdx]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {(program.roadmap12Months[quarter] || []).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                            qIdx === 0 ? 'bg-blue-100 text-blue-900' :
                            qIdx === 1 ? 'bg-green-100 text-green-900' :
                            qIdx === 2 ? 'bg-amber-100 text-amber-900' :
                            'bg-purple-100 text-purple-900'
                          }`}>
                            <span className="text-xs font-bold">{idx + 1}</span>
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <div className="space-y-4">
              {program?.faq && program.faq.length > 0 ? (
                program.faq.map((item, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <HelpCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">{item.question}</h3>
                          <p className="text-gray-600">{item.answer}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No FAQ items available.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Bottom Actions */}
        <div className="mt-8 flex justify-between items-center">
          <Button variant="outline" onClick={() => navigate('/catalyst/history')}>
            View History
          </Button>
          <Button onClick={() => navigate('/catalyst/wizard')} className="bg-blue-900 hover:bg-blue-800">
            Run New Assessment
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CatalystResults;
